import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Play, ArrowLeft, BarChart3, Cloud, Gauge, MessageSquareText, Save, ChevronLeft, ChevronRight, GripVertical, Sparkles } from "lucide-react";
import { AiQuestionsPanel } from "@/components/airep/AiQuestionsPanel";
import api from "@/lib/api";
import { yellowBtn, TYPES } from "@/lib/airep";

const ICONS = { poll: BarChart3, wordcloud: Cloud, scale: Gauge, open: MessageSquareText };
const blank = (type = "poll") => ({ id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()), type, question: "", options: type === "poll" ? ["", ""] : [] });

export default function AiReportEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deck, setDeck] = useState(null);
  const [cur, setCur] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [drag, setDrag] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const latest = useRef(null);
  latest.current = deck;
  useEffect(() => { api.get(`/airep/decks/${id}`).then(({ data }) => setDeck((prev) => (prev?.id === data.id ? prev : data))); }, [id]);
  useEffect(() => {
    if (!dirty) { return undefined; }
    const t = setTimeout(async () => {
      const snap = latest.current;
      setSaving(true);
      try { await api.put(`/airep/decks/${id}`, { title: snap.title, slides: snap.slides }); if (latest.current === snap) setDirty(false); }
      catch { toast.error("Salvataggio automatico non riuscito"); }
      finally { setSaving(false); }
    }, 1200);
    return () => clearTimeout(t);
  }, [deck, dirty, id]);
  useEffect(() => {
    if (!dirty) { return undefined; }
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  if (!deck) return null;
  const slide = deck.slides[cur] || deck.slides[0];
  const upd = (patch) => { setDirty(true); setDeck({ ...deck, ...patch }); };
  const updSlide = (patch) => upd({ slides: deck.slides.map((s, i) => (i === cur ? { ...s, ...patch } : s)) });
  const save = async (quiet) => {
    const { data } = await api.put(`/airep/decks/${id}`, { title: deck.title, slides: deck.slides });
    setDeck(data); setDirty(false); if (!quiet) toast.success("Presentazione salvata");
  };
  const present = async () => {
    if (deck.slides.some((s) => !s.question.trim())) return toast.error("Ogni slide deve avere una domanda");
    await save(true);
    try { const { data } = await api.post(`/airep/decks/${id}/start`); navigate(`/ai-report/present/${data.id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Avvio non riuscito"); }
  };
  const addAi = (slides) => {
    const keep = deck.slides.filter((s) => s.question.trim());
    upd({ slides: [...keep, ...slides] });
    setCur(keep.length);
    setAiOpen(false);
  };
  const add = () => { upd({ slides: [...deck.slides, blank()] }); setCur(deck.slides.length); };
  const del = () => { if (deck.slides.length < 2) return; upd({ slides: deck.slides.filter((_, i) => i !== cur) }); setCur(Math.max(0, cur - 1)); };
  const move = (from, to) => {
    if (to < 0 || to >= deck.slides.length || from === to) return;
    const arr = [...deck.slides];
    const [it] = arr.splice(from, 1);
    arr.splice(to, 0, it);
    upd({ slides: arr });
    setCur(to);
  };
  const inp = "w-full bg-white border border-slate-200 focus:border-slate-900 outline-none rounded-xl px-4 py-3 text-base";
  return (
    <div className="max-w-5xl space-y-5 pb-24" data-testid="airep-editor">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/ai-report/app" onClick={async (e) => { if (!dirty) return; e.preventDefault(); try { await save(true); navigate("/ai-report/app"); } catch { if (window.confirm("Salvataggio non riuscito. Uscire perdendo le modifiche?")) navigate("/ai-report/app"); } }} className="h-10 w-10 grid place-items-center rounded-xl bg-[#E3FF00]" data-testid="editor-back"><ArrowLeft className="h-4 w-4" /></Link>
        <input value={deck.title} onChange={(e) => upd({ title: e.target.value })} className="flex-1 min-w-[200px] font-display text-2xl font-bold bg-transparent outline-none border-b-2 border-transparent focus:border-slate-900" data-testid="editor-title-input" />
        <span className="text-xs text-slate-500" data-testid="editor-save-status">{saving ? "Salvataggio…" : dirty ? "Modifiche non salvate" : "Tutto salvato"}</span>
        <button onClick={() => save()} disabled={!dirty} className={`${yellowBtn} px-4 py-2.5 text-sm`} data-testid="editor-save"><Save className="h-4 w-4" /> Salva</button>
        <button onClick={present} className={`${yellowBtn} px-4 py-2.5 text-sm`} data-testid="editor-present"><Play className="h-4 w-4" /> Presenta</button>
      </div>
      {aiOpen ? <AiQuestionsPanel deckId={id} onResult={addAi} onClose={() => setAiOpen(false)} />
        : <button onClick={() => setAiOpen(true)} className={`${yellowBtn} px-4 py-2.5 text-sm`} data-testid="editor-ai-questions"><Sparkles className="h-4 w-4" /> Genera domande con AI</button>}
      <p className="text-xs text-slate-500">Trascina le schede per cambiare l'ordine delle domande (da telefono usa le frecce sulla scheda selezionata).</p>
      <div className="flex gap-2 overflow-x-auto pb-1 -mt-3" data-testid="editor-slide-tray">
        {deck.slides.map((s, i) => {
          const I = ICONS[s.type];
          return (
            <div key={s.id} draggable onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { move(drag, i); setDrag(null); }} onDragEnd={() => setDrag(null)}
              onClick={() => setCur(i)} data-testid={`editor-slide-${i}`}
              className={`shrink-0 w-40 text-left rounded-xl border p-3 cursor-grab active:cursor-grabbing transition-[border-color,opacity] ${drag === i ? "opacity-40" : ""} ${i === cur ? "border-slate-900 bg-white shadow-sm" : "border-slate-200 bg-white/60 hover:border-slate-400"}`}>
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500"><GripVertical className="h-3.5 w-3.5" /><I className="h-3.5 w-3.5" />{i + 1}
                {i === cur && <span className="ml-auto flex gap-1">
                  <button onClick={(e) => { e.stopPropagation(); move(i, i - 1); }} disabled={i === 0} className="h-6 w-6 grid place-items-center rounded-md bg-[#E3FF00] disabled:opacity-30" aria-label="Sposta a sinistra" data-testid={`editor-move-left-${i}`}><ChevronLeft className="h-3.5 w-3.5" /></button>
                  <button onClick={(e) => { e.stopPropagation(); move(i, i + 1); }} disabled={i === deck.slides.length - 1} className="h-6 w-6 grid place-items-center rounded-md bg-[#E3FF00] disabled:opacity-30" aria-label="Sposta a destra" data-testid={`editor-move-right-${i}`}><ChevronRight className="h-3.5 w-3.5" /></button>
                </span>}
              </div>
              <div className="text-sm font-medium truncate mt-1">{s.question || "Nuova domanda"}</div>
            </div>
          );
        })}
        <button onClick={add} className={`${yellowBtn} shrink-0 w-36 py-3 text-sm`} data-testid="editor-add-slide"><Plus className="h-4 w-4" /> Slide</button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-7 space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="editor-type-switch">
          {Object.entries(TYPES).map(([k, l]) => {
            const I = ICONS[k];
            return (
              <button key={k} onClick={() => updSlide({ type: k, options: k === "poll" ? (slide.options.length ? slide.options : ["", ""]) : [] })} data-testid={`editor-type-${k}`}
                className={`rounded-xl border-2 p-3 flex flex-col items-center gap-1.5 text-sm font-semibold transition-colors ${slide.type === k ? "border-slate-900 bg-[#E3FF00]" : "border-slate-200 hover:border-slate-400"}`}>
                <I className="h-5 w-5" />{l}
              </button>
            );
          })}
        </div>
        <input className={inp + " font-display text-xl font-bold"} placeholder="Scrivi la domanda…" value={slide.question} onChange={(e) => updSlide({ question: e.target.value })} maxLength={200} data-testid="editor-question-input" />
        {slide.type === "poll" && (
          <div className="space-y-2">
            {slide.options.map((o, i) => (
              <div key={i} className="flex gap-2">
                <input className={inp} placeholder={`Opzione ${i + 1}`} value={o} maxLength={80} onChange={(e) => updSlide({ options: slide.options.map((x, j) => (j === i ? e.target.value : x)) })} data-testid={`editor-option-${i}`} />
                <button onClick={() => updSlide({ options: slide.options.filter((_, j) => j !== i) })} disabled={slide.options.length < 3} className={`${yellowBtn} px-3`} data-testid={`editor-option-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {slide.options.length < 8 && <button onClick={() => updSlide({ options: [...slide.options, ""] })} className={`${yellowBtn} px-4 py-2 text-sm`} data-testid="editor-add-option"><Plus className="h-4 w-4" /> Opzione</button>}
          </div>
        )}
        {slide.type === "scale" && <p className="text-sm text-slate-500">Il pubblico sceglie un voto da 1 a 5; sullo schermo compaiono media e distribuzione.</p>}
        {slide.type === "wordcloud" && <p className="text-sm text-slate-500">Ogni persona invia una risposta: le parole più scelte diventano più grandi.</p>}
        {slide.type === "open" && <p className="text-sm text-slate-500">Risposte libere fino a 280 caratteri (una a persona). Perfetta per le domande alla platea.</p>}
        <button onClick={del} disabled={deck.slides.length < 2} className={`${yellowBtn} px-4 py-2 text-sm`} data-testid="editor-delete-slide"><Trash2 className="h-4 w-4" /> Elimina slide</button>
      </div>
    </div>
  );
}
